function isVisibleDialog(el: Element): boolean {
  const style = window.getComputedStyle(el);
  return (
    style.display !== "none" &&
    style.visibility !== "hidden" &&
    parseFloat(style.opacity) > 0.01
  );
}

function hasVisibleOpenDialog(): boolean {
  return Array.from(document.querySelectorAll('[role="dialog"]')).some(
    isVisibleDialog,
  );
}

export function hasOrphanedChakraModalLayers(): boolean {
  if (hasVisibleOpenDialog()) {
    return false;
  }

  return (
    document.querySelectorAll(".chakra-modal__content-container").length > 0 ||
    document.querySelectorAll(".chakra-modal__overlay").length > 0
  );
}

function isInsideVisibleDialog(el: Element | null): boolean {
  if (!el) return false;

  const dialog = el.closest('[role="dialog"]');
  if (dialog && isVisibleDialog(dialog)) {
    return true;
  }

  const portal = el.closest(".chakra-portal");
  if (portal) {
    return Array.from(portal.querySelectorAll('[role="dialog"]')).some(isVisibleDialog);
  }

  return false;
}

/** True only for stale modal portal layers, not for clicks inside an open dialog. */
export function isChakraModalBlockingElement(el: Element | null): boolean {
  if (!el) return false;
  if (isInsideVisibleDialog(el)) {
    return false;
  }

  return (
    el.classList.contains("chakra-modal__overlay") ||
    el.classList.contains("chakra-modal__content-container") ||
    !!el.closest(".chakra-modal__content-container")
  );
}

function restoreModalAccessibilityLock() {
  document
    .querySelectorAll("[aria-hidden], [data-aria-hidden]")
    .forEach((el) => {
      el.removeAttribute("aria-hidden");
      el.removeAttribute("data-aria-hidden");
    });

  document.querySelectorAll("[inert]").forEach((el) => {
    el.removeAttribute("inert");
  });

  const root = document.getElementById("root");
  if (root) {
    root.removeAttribute("aria-hidden");
    root.removeAttribute("data-aria-hidden");
    root.removeAttribute("inert");
    if (root instanceof HTMLElement) {
      root.style.pointerEvents = "";
    }
  }

  document.body.style.pointerEvents = "";
  document.body.classList.remove("chakra-modal--open");
}

/** Remove Chakra modal portal layers left behind when exit animation does not finish. */
export function purgeOrphanedChakraModalLayers(options?: { force?: boolean }) {
  const force = options?.force ?? false;
  const visibleDialogOpen = hasVisibleOpenDialog();

  if (!force && visibleDialogOpen) {
    return;
  }

  document.querySelectorAll(".chakra-modal__overlay").forEach((el) => {
    const portal = el.closest(".chakra-portal");
    const portalHasVisibleDialog =
      portal &&
      Array.from(portal.querySelectorAll('[role="dialog"]')).some(isVisibleDialog);

    if (!portalHasVisibleDialog) {
      if (el instanceof HTMLElement) {
        el.style.pointerEvents = "none";
      }
      el.remove();
    }
  });

  document.querySelectorAll(".chakra-modal__content-container").forEach((el) => {
    const hasVisibleChild = Array.from(
      el.querySelectorAll('[role="dialog"]'),
    ).some(isVisibleDialog);

    if (!hasVisibleChild) {
      if (el instanceof HTMLElement) {
        el.style.pointerEvents = "none";
      }
      const portal = el.closest(".chakra-portal");
      (portal ?? el).remove();
    }
  });

  document.querySelectorAll('[role="dialog"]').forEach((dialog) => {
    if (!isVisibleDialog(dialog)) {
      dialog.closest(".chakra-portal")?.remove() ?? dialog.remove();
    }
  });

  document.querySelectorAll(".chakra-portal").forEach((portal) => {
    const hasVisible = Array.from(
      portal.querySelectorAll('[role="dialog"]'),
    ).some(isVisibleDialog);
    if (!hasVisible) {
      portal.remove();
    }
  });

  if (!hasVisibleOpenDialog()) {
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
    document.body.removeAttribute("data-scroll-locked");
    document.documentElement.classList.remove("modal-open-hide-scrollbar");
    document.body.classList.remove("modal-open-hide-scrollbar");
    document.body.removeAttribute("data-__igniteOpenModalCount");
    restoreModalAccessibilityLock();
  }
}

/** Run purge after Chakra modal close effects have committed. */
export function schedulePurgeOrphanedChakraModalLayers() {
  requestAnimationFrame(() => {
    purgeOrphanedChakraModalLayers({ force: true });
    window.setTimeout(() => purgeOrphanedChakraModalLayers({ force: true }), 50);
  });
}

/** Continuously remove modal portal debris when no dialog is visibly open. */
export function startChakraModalGhostLayerGuard(): () => void {
  let scheduled = false;

  const sweep = () => {
    scheduled = false;
    if (hasOrphanedChakraModalLayers()) {
      purgeOrphanedChakraModalLayers({ force: true });
    }
  };

  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(sweep);
  });

  observer.observe(document.body, { childList: true, subtree: true });
  return () => observer.disconnect();
}

/** After removing a ghost modal layer, forward the click to the element underneath. */
export function forwardClickThroughGhostLayer(
  clientX: number,
  clientY: number,
): boolean {
  purgeOrphanedChakraModalLayers({ force: true });

  const underneath = document.elementFromPoint(clientX, clientY);
  if (!(underneath instanceof HTMLElement)) return false;
  if (isChakraModalBlockingElement(underneath)) return false;

  const clickable =
    underneath.closest<HTMLElement>(
      'button, a[href], [role="button"], [data-cy], input:not([disabled]), select, textarea, label',
    ) ?? underneath;

  const eventInit: MouseEventInit = {
    bubbles: true,
    cancelable: true,
    clientX,
    clientY,
    view: window,
  };

  clickable.dispatchEvent(new MouseEvent("mousedown", eventInit));
  clickable.dispatchEvent(new MouseEvent("mouseup", eventInit));
  clickable.click();
  return true;
}
